import { Injectable } from '@angular/core';
import { ServiceBase, OrigamiResponse } from './base/service.base';
import { EventTypeCategory } from '@app/origami-api/constants/event-type-category';
import { Observable } from 'rxjs';
import { TopicField } from '../utils/topic-utils';
import { ErrorCode } from '@app/origami-api/constants/error-codes';
import {
  RequiredDocumentServiceModel,
  RequiredDocumentServiceOptions,
} from '@app/origami-api/services/required-document.service';
import { RequiredProvidedLookup } from '@app/origami-api/constants/required-documents';

export interface GetEvents {
  eventFilter: EventFilter;
  limit?: number;
  orderBy?: string;
  orderByClause?: string;
  siteId: number;
  start?: number;
}

export interface GetEventViews {
  eventFilter: EventFilter;
  siteId: number;
}

export interface EventFilter {
  RequiredDocumentFields?: TopicField[];
  InvestigativeSiteId?: number;
  CountryCode?: string;
  EventTypeCategory?: number;
  GetMode?: number;
  EventTopicId?: number;
  EventTypeId?: number;
  StatusId?: number;
  Filter?: number;
  ForDocumentAssignment?: boolean;
  SearchClause?: string;
}

export interface TopicWorkflowStageData {
  Comments: string;
  IndexPosition: number; // TODO
  Issues: string;
  StatusId: number;
  WorkflowStageId: number;
}

export interface GetEventsResult {
  Events: EventsItem[];
  TotalRowCount: number;
}

export interface GetEventViewsResult {
  EntitiesCount: number;
  ViewFilterDisplayName: string;
  ViewFilterId: number;
}

export interface EventsItem {
  Countries?: Countries[];
  CreatedBy?: number;
  CreatedDate?: string;
  Documents?: string;
  InvestigativeSites?: InvestigativeSites[];
  LockedBy?: number;
  ParentId?: number;
  SiteId: number;
  TopicFields: TopicField[];
  TopicId?: number;
  TopicName: string;
  TopicTypeId?: number;
  TopicWorkflowStageFields?: TopicWorkflowStageData[];
  RequiredDocuments?: EventRequiredDocuments.RequiredDocuments[];
  SourceEventId?: number;
}

export namespace EventRequiredDocuments {
  export interface RequiredDocuments {
    DocumentTypeId: number;
    EventTopicId?: number;
    ProvidedBy: RequiredProvidedLookup;
    ProvidedById: number;
    RequiredBy: RequiredProvidedLookup;
    RequiredById: number;
    RequiredDocumentMetadata: any[];
    SiteId: number;
  }
}

export interface Countries {
  CountryCode: string;
  CreatedBy?: number;
  CreatedDate?: string;
  EventTopicId?: number;
  SiteId?: number;
}
export interface InvestigativeSites {
  InvestigativeSiteId: number;
  CreatedBy?: number;
  CreatedDate?: string;
  EventTopicId?: number;
  SiteId?: number;
}
export interface GetEventTypes {
  categoryFlag?: number;
  enabledOnly?: boolean;
  siteId: number;
}

export interface GetEventTypesResult {
  EventTypes: EventType[];
  TotalRowCount: number;
}

export interface GetEventType {
  eventTypeTopicId: number;
  siteId: number;
}

export interface EventTypes {
  EventTypes: EventType[];
}

export interface EventType {
  EventTypeDataRoomTopic: {
    CreatedBy?: number;
    CreatedDate?: string;
    LockedBy?: number;
    ParentId?: number;
    SiteId?: number;
    TopicFields: TopicField[];
    TopicId?: number;
    TopicName?: string;
    TopicTypeId?: number;
  };
  EventTypeDomainTopic: {
    CreatedBy?: number;
    CreatedDate?: string;
    LockedBy?: number;
    ParentId?: number;
    SiteId: number;
    TopicFields: TopicField[];
    TopicId?: number;
    TopicName?: string;
    TopicTypeId?: number;
  };
  DocumentTypes?: any;
}

export interface SaveEventsParams {
  createUpdateMilestoneTracks: MilestoneHistoryTrack[];
  deleteMilestoneHistoryIds: string[];
  flags: number;
  siteId: number;
}

export interface EditEventParams {
  siteId: number;
  eventTopic: EventsItem;
}
export interface CreateEventParams {
  siteId: number;
  events: EventsItem[];
}

export interface SaveEventType {
  siteId: number;
  eventType: EventType;
}

export interface DeleteEventTypesParams {
  siteId: number;
  eventTypeTopicsIds: number[];
}

export interface DeleteEventsParams {
  siteId: number;
  eventIds: [];
}

export interface MilestoneHistoryTrack {
  MilestoneId: number;
  PlannedDate?: string;
  MilestoneDate?: string;
  TrackId: number;
  Comments: string;
  MilestoneHistoryId?: number;
  InvestigativeSiteId?: string;
  CountryCode?: string;
}

export interface GetCurrentMilestone {
  siteId?: number;
}

export interface GetEventTriggerViews {
  siteId: number;
  viewMode?: number;
  viewFilter?: string;
}

export interface EventTriggerView {
  EntitiesCount: number;
  ViewFilterDisplayName: string;
  ViewFilterId: string;
}

export interface DeleteEventTriggersParams {
  siteId: number;
  topicIds: number[];
}

@Injectable()
export class EventManagementService extends ServiceBase {
  serviceUrl = 'EventManagementService.svc';

  getEventTypes(
    params: GetEventTypes,
    options?,
    silentErrorCodes?: ErrorCode[],
    forceRequest?: boolean,
  ): Observable<OrigamiResponse<GetEventTypesResult>> {
    switch (params.categoryFlag) {
      case EventTypeCategory.Study:
        params.categoryFlag = 1;
        break;
      case EventTypeCategory.Site:
        params.categoryFlag = 2;
        break;
      case EventTypeCategory.Country:
        params.categoryFlag = 4;
        break;
      default:
        params.categoryFlag = EventTypeCategory.Default;
    }

    return this.origamiPost('/GetEventTypes', params, options, silentErrorCodes || null, forceRequest ?? true);
  }

  createEventType(params: SaveEventType, options?, silentErrorCodes?: ErrorCode[]): Observable<OrigamiResponse<any[]>> {
    return this.origamiPost('/CreateEventType', params, options, silentErrorCodes || null, true);
  }

  saveEventType(params: SaveEventType, options?, silentErrorCodes?: ErrorCode[]): Observable<OrigamiResponse<any[]>> {
    return this.origamiPost('/SaveEventType', params, options, silentErrorCodes || null, true);
  }

  deleteEventTypes(
    params: DeleteEventTypesParams,
    options?,
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiResponse<any[]>> {
    return this.origamiPost('/DeleteEventTypes', params, options, silentErrorCodes || null, true);
  }

  getEvents(params: GetEvents): Observable<OrigamiResponse<GetEventsResult>> {
    return this.origamiPost('/GetEvents', params, null, null, true);
  }

  createEvents(params: CreateEventParams, silentErrorCodes?: ErrorCode[]): Observable<OrigamiResponse<any[]>> {
    return this.origamiPost('/CreateEvents', params, null, silentErrorCodes || null, true);
  }

  deleteEvents(
    params: DeleteEventsParams,
    options?,
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiResponse<any[]>> {
    return this.origamiPost('/DeleteEvents', params, options, silentErrorCodes || null, true);
  }

  editEvent(params: EditEventParams, options?, silentErrorCodes?: ErrorCode[]): Observable<OrigamiResponse<any[]>> {
    return this.origamiPost('/EditEvent', params, options, silentErrorCodes || null, true);
  }

  getDefaultRequiredDocumentsForEvent(
    body: RequiredDocumentServiceOptions.GetDefaultRequiredDocumentsForEvent,
  ): Observable<OrigamiResponse<RequiredDocumentServiceModel.RequiredDocuments>> {
    return this.origamiPost('/GetDefaultRequiredDocumentsForEvent', body, null, null, true);
  }

  getEventTriggerViews(body: GetEventTriggerViews): Observable<OrigamiResponse<EventTriggerView[]>> {
    return this.origamiPost('/GetEventTriggerViews', body, null, null, true);
  }

  getEventViews(params: GetEvents): Observable<OrigamiResponse<GetEventViewsResult[]>> {
    return this.origamiPost('/GetEventViews', params, null, null, true);
  }

  deleteEventTriggers(
    params: DeleteEventTriggersParams,
    options?,
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiResponse<any[]>> {
    return this.origamiPost('/DeleteEventTriggers', params, options, silentErrorCodes || null, true);
  }
}
